import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useFetcher, useOutletContext } from '@remix-run/react';

import StatusInputs from '~/components/Forms/StatusInputs';
import { usePageContext } from '~/context/usePageContext';
import useEmail from '~/hooks/useEmail';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { Publication } from '~/interfaces/publication';
import Continue from '~/pages/publications/components/Continue';
import InboxPhone from '~/pages/publications/components/InboxPhone';
import PublicationHeader from '~/pages/publications/components/PublicationHeader';
import PublicationListItem from '~/pages/publications/components/PublicationListItem';
import PublicationsList from '~/pages/publications/components/PublicationsList';
import useSelectedPubs from '~/pages/publications/hooks/useSelectedPubs';
import getPublication from '~/services/api/publication/getPublication.server';
import getPublications from '~/services/api/publication/getPublications.server';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const publication = data?.publication;
  const metaTitle = data?.metaTitle;

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !metaTitle,
    overrides: {
      title: metaTitle,
      description: `More newsletters from the team behind ${publication?.name}`,
      robots: 'noindex',
    },
  });
};

export const loader: LoaderFunction = async ({ request }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);

  const [publications, publication] = await Promise.all([
    getPublications(request, session),
    getPublication(request, session),
  ]);

  const hasMultiplePublications = publications?.length >= 1;

  if (!hasMultiplePublications) {
    return redirect('/');
  }

  return json({
    requestUrl: url.href,
    metaTitle: `Newsletters By ${publication?.name}`,
    publication,
    publications,
  });
};

const orderPubs = (listOfIds: string[], listOfPubs: any[]) => {
  const list1 = listOfPubs.filter((obj) => listOfIds.includes(obj.id));
  const list2 = listOfPubs.filter((obj) => !listOfIds.includes(obj.id));

  const sortedList1 = list1.sort((a, b) => listOfIds.indexOf(a.id) - listOfIds.indexOf(b.id));

  return [...sortedList1, ...list2];
};

interface PublicationsLoaderData {
  requestUrl: string;
  metaTitle: string;
  publication: Publication;
  publications: Publication[];
}

const Publications = () => {
  const { publication, publications } = useTypedLoaderData<PublicationsLoaderData>();
  const { publicationId } = useOutletContext<OutletContext>();
  const { t } = useTranslation();
  const { nextPage } = useSignupRedirects(publication);

  const [email, setEmail] = useEmail();
  const [promptForEmail, setPromptForEmail] = useState('');

  const { visitToken } = usePageContext();
  const fetcher = useFetcher<{
    toast: { message: string; status: string };
    status: string;
  }>();
  const data = fetcher.data;
  const isSuccessful = data?.status === 'success';

  useEffect(() => {
    if (isSuccessful) {
      nextPage();

      toast.success(data.toast.message);
    }
  }, [isSuccessful]);

  const { selectedPubsIds, onToggle, isAllSelected, handleBulkToggle } = useSelectedPubs({
    publicationIds: publications.map((p: Publication) => p.id),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const emailToSubmit = email || promptForEmail;
    const includeCurrentPublication = emailToSubmit === promptForEmail ? 'true' : 'false';

    if (emailToSubmit) {
      const formData = new FormData();
      formData.append('email', emailToSubmit);
      selectedPubsIds.forEach((id: string) => {
        formData.append('publication_ids[]', id);
      });
      formData.append('include_current_publication', includeCurrentPublication);
      formData.append('visit_token', visitToken || '');
      formData.append('publication_id', publicationId);
      formData.append('recommendations_error_message', t('errors.default'));
      formData.append('recommendations_success_message', t('recommendations.toast.success_one'));

      // HUMAN only monitors requests that match specific routes configured in the Cloudflare Worker.
      // These routes can be defined as exact matches or partial matches using wildcards.
      // To ensure this page's POST signup request is monitored, we would need to add a wildcard route `*/publications`.
      // However, this would match more than just the POST signup requests from this page,
      // leading to unnecessary monitoring and potentially exceeding our monthly limit of monitorable requests.
      // To resolve this issue, we're POSTing to a more uniquely named route `*/publications_subscribe_form`.
      fetcher.submit(formData, {
        method: 'post',
        action: '/publications_subscribe_form',
      });
      setEmail(emailToSubmit);
    } else {
      toast.error(t('recommendations.toast.error'));
    }
  };

  return (
    <>
      <div>
        <div className="relative h-96 w-full overflow-hidden border-b border-gray-200 bg-gray-50 px-4 sm:h-80">
          <div className="relative mx-auto flex h-full w-full max-w-4xl justify-between sm:items-center">
            <div className="hidden sm:flex">
              <PublicationHeader name={publication.name} logoUrl={publication.logo.url} />
            </div>

            <div className="absolute right-1/2 top-10 translate-x-1/2 transform sm:right-0 sm:top-12 sm:translate-x-0">
              <InboxPhone publication={publication} publications={publications} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 py-16 pb-56">
        <div className="mx-auto w-full max-w-6xl">
          <fetcher.Form className="relative w-full" onSubmit={onSubmit}>
            <StatusInputs
              name="recommendations"
              errorMessage={t('errors.default')}
              successMessage={t('recommendations.toast.success_one')}
            />
            <PublicationsList>
              {orderPubs(publication.web_template.multi_pub_ids, publications).map((publication: Publication) => (
                <PublicationListItem
                  key={publication.id}
                  selected={selectedPubsIds.includes(publication.id)}
                  onToggle={onToggle}
                  publication={publication}
                />
              ))}
            </PublicationsList>
            <Continue
              isAllSelected={isAllSelected}
              onToggle={handleBulkToggle}
              handleRedirect={nextPage}
              email={email}
              promptForEmail={promptForEmail}
              setPromptForEmail={setPromptForEmail}
            >
              {selectedPubsIds.length ? `(${selectedPubsIds.length}) ` : ''}
              {publication.cta_text || t('shared.cta.subscribe')}
            </Continue>
          </fetcher.Form>
        </div>
      </div>
    </>
  );
};

export default Publications;
